/**
 * §P.1 · Alta y edición de un pagador.
 *
 * Dos cosas que este formulario hace a propósito:
 *
 * **`user_id` no es obligatorio, pero su ausencia se dice.** Un pagador sin cuenta
 * vinculada es legítimo —una empresa, un abuelo sin móvil— y a la vez es un recibo
 * que la familia no va a ver en la app. Dejarlo vacío en silencio es lo que hace
 * que un club descubra en enero que ninguna madre ve sus recibos.
 *
 * **`PATCH` exige `If-Match`.** Los datos fiscales acaban en un documento
 * contable, así que la precondición aquí no es una formalidad: dos personas
 * editando el NIF de la misma familia es un recibo mal emitido.
 */
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import {
  camposQueCambiaron,
  esConflictoDeEdicion,
  etagDe,
} from '@/shared/api/concurrencia';
import { aProblema, ErrorDeApi, type Problema } from '@/shared/api/errores';
import { Error as PanelError } from '@/shared/ui/Estados';
import type { Pagador } from '@/club/pagadores/tipos';

interface Campos {
  legal_name: string;
  tax_id: string;
  email: string;
  phone: string;
  address_line: string;
  postal_code: string;
  city: string;
}

const ETIQUETAS: Record<string, string> = {
  legal_name: 'el nombre',
  tax_id: 'el NIF',
  email: 'el correo',
  phone: 'el teléfono',
  address_line: 'la dirección',
  postal_code: 'el código postal',
  city: 'la localidad',
  user_id: 'la cuenta vinculada',
};

const desde = (p: Pagador | null): Campos => ({
  legal_name: p?.legal_name ?? '',
  tax_id: p?.tax_id ?? '',
  email: p?.email ?? '',
  phone: p?.phone ?? '',
  address_line: p?.address_line ?? '',
  postal_code: p?.postal_code ?? '',
  city: p?.city ?? '',
});

const oNulo = (v: string): string | null => (v.trim() === '' ? null : v.trim());

/** Datos con los que llega prerrellenado desde un tutor de la ficha del alumno. */
export interface DesdeTutor {
  readonly user_id: string;
  readonly legal_name: string;
  readonly email: string | null;
}

export function FormularioDePagador({
  club,
  pagador,
  etag,
  desdeTutor,
  suspendido,
  alGuardar,
  alCancelar,
}: {
  club: string;
  /** `null` para un alta. */
  pagador: Pagador | null;
  etag: string | null;
  /** Si viene de un tutor, su cuenta y sus datos. */
  desdeTutor?: DesdeTutor | undefined;
  suspendido: boolean;
  alGuardar: (pagadorId: string) => void;
  alCancelar: () => void;
}) {
  const cache = useQueryClient();
  const editando = pagador !== null;

  const [campos, setCampos] = useState<Campos>(() => {
    const base = desde(pagador);
    if (!editando && desdeTutor) {
      return {
        ...base,
        legal_name: desdeTutor.legal_name,
        email: desdeTutor.email ?? '',
      };
    }
    return base;
  });

  const [tocados, setTocados] = useState<Set<string>>(new Set());
  const [problema, setProblema] = useState<Problema | null>(null);
  const [conflicto, setConflicto] = useState<readonly string[] | null>(null);
  const [etagRecibido, setEtagRecibido] = useState(etag);
  const [etagVivo, setEtagVivo] = useState(etag);

  if (etag !== etagRecibido) {
    setEtagRecibido(etag);
    setEtagVivo(etag);
  }

  // La cuenta vinculada: la del tutor de origen, o la que ya tenía el pagador.
  const cuenta = desdeTutor?.user_id ?? pagador?.user_id ?? null;

  const cambiar = (campo: keyof Campos, valor: string) => {
    setCampos((c) => ({ ...c, [campo]: valor }));
    setTocados((t) => new Set(t).add(campo));
  };

  const guardar = useMutation({
    mutationFn: async () => {
      const cuerpo = {
        legal_name: campos.legal_name.trim(),
        tax_id: oNulo(campos.tax_id),
        email: oNulo(campos.email),
        phone: oNulo(campos.phone),
        address_line: oNulo(campos.address_line),
        postal_code: oNulo(campos.postal_code),
        city: oNulo(campos.city),
      };

      if (!editando) {
        const r = await api.v1.clubs({ club }).payers.post({
          ...cuerpo,
          // Se manda solo si hay: `null` explícito es tan válido como omitirlo,
          // pero omitirlo deja claro que no se está desvinculando nada.
          ...(cuenta ? { user_id: cuenta } : {}),
        });
        if (r.error) throw new ErrorDeApi(aProblema(r.error));
        return { pagador: r.data as unknown as Pagador, etag: etagDe(r) };
      }

      const r = await api.v1
        .clubs({ club })
        .payers({ payerId: pagador.id })
        .patch(cuerpo, {
          headers: etagVivo ? { 'if-match': etagVivo } : {},
        });
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return { pagador: r.data as unknown as Pagador, etag: etagDe(r) };
    },

    onSuccess: ({ pagador: guardado, etag: nuevo }) => {
      setProblema(null);
      setConflicto(null);
      setTocados(new Set());
      if (nuevo) setEtagVivo(nuevo);

      void cache.invalidateQueries({ queryKey: ['pagadores', club] });
      void cache.invalidateQueries({
        queryKey: ['pagador', club, guardado.id],
      });

      alGuardar(guardado.id);
    },

    onError: async (error) => {
      const p = aProblema(error);

      if (!esConflictoDeEdicion(p) || !editando) {
        setProblema(p);
        return;
      }

      const r = await api.v1
        .clubs({ club })
        .payers({ payerId: pagador.id })
        .get();

      if (r.error || !r.data) {
        setProblema(p);
        return;
      }

      const ficha = r.data as unknown as { payer: Pagador };
      setEtagVivo(etagDe(r));
      setConflicto(
        camposQueCambiaron(
          pagador as unknown as Record<string, unknown>,
          ficha.payer as unknown as Record<string, unknown>,
          [...tocados]
        )
      );
    },
  });

  if (suspendido) {
    return (
      <div className="hueco">
        <h2>La cuenta del club está suspendida</h2>
        <p>
          Puedes consultar los pagadores y sus recibos, pero no guardar cambios
          hasta que se resuelva el impago.
        </p>
        <div className="hueco-accion">
          <button type="button" className="btn-plano" onClick={alCancelar}>
            Volver
          </button>
        </div>
      </div>
    );
  }

  return (
    <form
      className="formulario"
      onSubmit={(e) => {
        e.preventDefault();
        guardar.mutate();
      }}
    >
      <h1>{editando ? 'Editar pagador' : 'Nuevo pagador'}</h1>

      {conflicto && (
        <div className="aviso" role="status">
          <strong>
            Alguien ha cambiado este pagador mientras lo editabas.
          </strong>{' '}
          {conflicto.length > 0 ? (
            <>
              Cambió {conflicto.map((c) => ETIQUETAS[c] ?? c).join(', ')}. Lo
              tuyo sigue escrito abajo: si le das a guardar otra vez, se queda
              tu versión.
            </>
          ) : (
            <>
              Los cambios no afectan a lo que estás tocando, así que puedes
              guardar otra vez sin perder nada.
            </>
          )}
        </div>
      )}

      <div className="rejilla">
        <label>
          Nombre o razón social
          <input
            required
            maxLength={160}
            value={campos.legal_name}
            onChange={(e) => cambiar('legal_name', e.target.value)}
          />
        </label>

        <label>
          NIF o CIF
          <input
            maxLength={20}
            placeholder="Opcional"
            value={campos.tax_id}
            onChange={(e) => cambiar('tax_id', e.target.value)}
          />
        </label>

        <label>
          Correo
          <input
            type="email"
            value={campos.email}
            onChange={(e) => cambiar('email', e.target.value)}
          />
        </label>

        <label>
          Teléfono
          <input
            value={campos.phone}
            onChange={(e) => cambiar('phone', e.target.value)}
          />
        </label>

        <label>
          Dirección
          <input
            value={campos.address_line}
            onChange={(e) => cambiar('address_line', e.target.value)}
          />
        </label>

        <label>
          Código postal
          <input
            maxLength={10}
            value={campos.postal_code}
            onChange={(e) => cambiar('postal_code', e.target.value)}
          />
        </label>

        <label>
          Localidad
          <input
            value={campos.city}
            onChange={(e) => cambiar('city', e.target.value)}
          />
        </label>
      </div>

      <CuentaVinculada cuenta={cuenta} deTutor={desdeTutor} />

      {problema && (
        <PanelError problema={problema} reintentar={() => guardar.mutate()} />
      )}

      <div className="acciones">
        <button type="submit" disabled={guardar.isPending}>
          {guardar.isPending
            ? 'Guardando…'
            : editando
              ? 'Guardar cambios'
              : 'Crear pagador'}
        </button>
        <button
          type="button"
          className="btn-plano"
          onClick={alCancelar}
          disabled={guardar.isPending}
        >
          Cancelar
        </button>
      </div>
    </form>
  );
}

/**
 * La cuenta vinculada, explicada.
 *
 * No es un campo editable: una cuenta no se teclea, se elige de entre los tutores
 * del alumno —que es donde están, porque `GET /memberships` es solo del personal y
 * «los tutores no salen ahí»—. Lo que hace este bloque es **decir qué falta**.
 */
function CuentaVinculada({
  cuenta,
  deTutor,
}: {
  cuenta: string | null;
  deTutor: DesdeTutor | undefined;
}) {
  if (cuenta) {
    return (
      <div className="nota nota-bien">
        <strong>Vinculado a una cuenta de la app.</strong>{' '}
        {deTutor
          ? `Es la de ${deTutor.legal_name}, tutora del alumno desde el que has entrado.`
          : 'Los recibos de este pagador se ven en la app de familias.'}
      </div>
    );
  }

  return (
    <div className="nota">
      <strong>Sin cuenta de la app.</strong> Este pagador va a recibir recibos
      que <strong>nadie verá en el móvil</strong>: es lo correcto para una
      empresa o para quien paga y no quiere app, y es un problema para una
      madre.
      <br />
      Para vincularla, crea el pagador desde la ficha de su hijo o hija, en el
      tutor correspondiente.
    </div>
  );
}
